import React, { useState } from 'react';
import {
  Skeleton, Stack, Typography, useMediaQuery,
} from '@mui/material';
import {
  useFetchImmutable, useApiClient,
  BackButton,
  ContextMenuButton,
  ContextMenu,
  ContextMenuItem,
  useContextMenu,
} from '@marageti/z4-lib';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { ViewProps } from './types';
import { formatCountryCodes } from '../../utils/helpers';
import SubNavigation from '../sub-navigation';
import TripMatchChipList from '../chip-list/trip-match';
import { getChipsFromTripMatch } from '../../utils/chips';
import CancelSoldTripModal from '../modals/cancel-sold-trip-modal';

const TripMatchSubNavigationView = ({
  tabs,
  tripMatch,
  traveler,
  onSoldCanceled,
}: ViewProps) => {
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const partnerName = tripMatch ? tripMatch.partnerName : <Skeleton width={150} />;
  const countries = tripMatch?.countries && countryCodes ? formatCountryCodes(tripMatch.countries, countryCodes) : <Skeleton width={100} />;

  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('desktop'));

  const [contextMenuAnchor, setContextMenuAnchor, contextMenuOpen] = useContextMenu();
  const [cancelSoldOpen, setCancelSoldOpen] = useState(false);
  const isSold = tripMatch?.status === TripMatchStatus.MatchSold;
  const menuButtonId = 'tm-sold-context-button';
  const menuId = 'tm-sold-context-menu';

  const cancelSoldAdornment = isSold ? (
    <>
      <ContextMenuButton
        id={menuButtonId}
        open={contextMenuOpen}
        onClick={(e) => setContextMenuAnchor(e.currentTarget)}
        menuId={menuId}
      />
      <ContextMenu
        anchorEl={contextMenuAnchor}
        buttonId={menuButtonId}
        id={menuId}
        onClose={() => setContextMenuAnchor(null)}
        open={contextMenuOpen}
      >
        <ContextMenuItem
          onClick={() => {
            setCancelSoldOpen(true);
            setContextMenuAnchor(null);
          }}
        >
          Cancel Sold Trip
        </ContextMenuItem>
      </ContextMenu>
      <CancelSoldTripModal
        open={cancelSoldOpen}
        onClose={() => setCancelSoldOpen(false)}
        tripMatchId={tripMatch.id}
        onSuccess={onSoldCanceled}
      />
    </>
  ) : undefined;

  const travelerNameElement = traveler?.fullName ? (
    <Typography
      component="span"
      variant="h3"
      fontWeight="bold"
      color="primary.main"
    >
      {traveler?.fullName}
    </Typography>
  ) : <Skeleton width={250} />;

  const partnerAndCountriesElement = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography
        component="a"
        variant="caption"
        href={`/partners/${tripMatch?.partnerId}`}
        target="_blank"
        sx={{
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        {partnerName}
      </Typography>
      <Typography variant="caption">•</Typography>
      <Typography variant="caption">{countries}</Typography>
    </Stack>
  );

  const chipListElement = <TripMatchChipList chips={tripMatch ? getChipsFromTripMatch(tripMatch) : []} />;

  return (
    <SubNavigation
      profile={{
        primary: isDesktop ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {travelerNameElement}
            {chipListElement}
          </Stack>
        ) : (
          travelerNameElement
        ),
        secondary: partnerAndCountriesElement,
        ...(!isDesktop && { decoration: chipListElement }),
      }}
      startAdornment={(
        <BackButton
          aria-label="back to trip matches"
          defaultPath="/trip-matches"
          edge="start"
        />
      )}
      endAdornment={cancelSoldAdornment}
      tabs={tabs}
    />
  );
};

export default TripMatchSubNavigationView;
